import {
  getMessageUploadKind,
  isMessageUploadDeclaredSizeAllowed,
  isUploadableTextFile,
  normalizeMessageUploadMediaType,
  sanitizeUploadFilename,
  validateMessageUpload,
  type ChatAttachment,
} from "@brains/plugins";
import type { Message } from "chat";
import type { ChatThread, ChatUploadStore } from "./types";

export interface AgentInput {
  message: string;
  attachments: ChatAttachment[];
  notices: string[];
}

interface ThreadIdParts {
  guildId?: string;
  channelId?: string;
  threadId?: string;
}

export type ChatFetch = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

interface ChatInputBuilderDeps {
  /** Return the platform's scoped upload store, or undefined when ingestion is unsupported. */
  getUploadStore: (platform: string) => ChatUploadStore | undefined;
  getThreadIdParts: (threadId: string) => ThreadIdParts;
  logger: {
    error: (message: string, context?: Record<string, unknown>) => void;
  };
  /** Downloads URL-only attachments. Defaults to the global fetch. */
  fetch?: ChatFetch | undefined;
}

/**
 * Turns an incoming chat message into agent input: validates and stores each
 * file attachment (collecting notices for rejects), producing the message text
 * plus ChatAttachments. Platform-agnostic — the platform-scoped upload store
 * is injected, while each adapter supplies its own authenticated fetchData().
 */
export class ChatInputBuilder {
  private readonly deps: ChatInputBuilderDeps;

  constructor(deps: ChatInputBuilderDeps) {
    this.deps = deps;
  }

  async build(
    platform: string,
    thread: ChatThread,
    message: Message,
    userLevel: string,
  ): Promise<AgentInput> {
    const agentInput: AgentInput = {
      message: normalizeIncomingMessageText(platform, message),
      attachments: [],
      notices: [],
    };
    if (message.attachments.length === 0) return agentInput;

    const canUpload = userLevel === "admin" || userLevel === "trusted";
    if (!canUpload) return agentInput;

    const uploadStore = this.deps.getUploadStore(platform);
    if (!uploadStore) return agentInput;

    for (const attachment of message.attachments) {
      const attachmentName = attachment.name;
      if (!attachmentName) continue;
      const filename = sanitizeUploadFilename(attachmentName, "upload");
      const mediaType = normalizeMessageUploadMediaType(
        filename,
        attachment.mimeType,
      );
      const declaredSize = attachment.size ?? 0;
      const uploadKind = getMessageUploadKind(filename, mediaType);
      if (!uploadKind) {
        agentInput.notices.push(`Unsupported file upload type: ${filename}`);
        continue;
      }
      if (!isMessageUploadDeclaredSizeAllowed(uploadKind, declaredSize)) {
        agentInput.notices.push(`File upload too large: ${filename}`);
        continue;
      }

      try {
        const content = await this.readAttachmentData(attachment);
        if (!content) continue;
        const validation = validateMessageUpload({
          filename,
          mediaType,
          content,
          fallbackFilename: "upload",
        });
        if (!validation.ok) {
          agentInput.notices.push(validation.message);
          continue;
        }
        const record = await uploadStore.save({
          filename: validation.filename,
          mediaType: validation.mediaType,
          content,
          metadata: this.buildMetadata(platform, thread, message),
        });
        agentInput.attachments.push(
          toChatAttachment(
            record.filename,
            record.mediaType,
            content,
            record.ref,
            validation.kind === "text",
          ),
        );
      } catch (error: unknown) {
        this.deps.logger.error("Failed to read chat attachment", {
          error,
          filename,
        });
        agentInput.notices.push(`Could not read file upload: ${filename}`);
      }
    }

    return agentInput;
  }

  private async readAttachmentData(
    attachment: Message["attachments"][number],
  ): Promise<Buffer | undefined> {
    if (attachment.fetchData) return attachment.fetchData();
    if (!attachment.url) return undefined;

    const response = await (this.deps.fetch ?? fetch)(attachment.url);
    if (!response.ok) {
      throw new Error(
        `Attachment download failed with status ${response.status}`,
      );
    }
    const data = await response.arrayBuffer();
    return Buffer.from(new Uint8Array(data));
  }

  private buildMetadata(
    platform: string,
    thread: ChatThread,
    message: Message,
  ): Record<string, unknown> {
    const ids = this.deps.getThreadIdParts(thread.id);
    return {
      interfaceType: platform,
      channelId: thread.id,
      parentChannelId: thread.channelId,
      messageId: message.id,
      uploaderId: message.author.userId,
      uploaderUsername: message.author.userName,
      ...(ids.guildId ? { guildId: ids.guildId } : {}),
      ...(ids.threadId ? { threadId: ids.threadId } : {}),
    };
  }
}

/** Build a ChatAttachment from already-stored upload bytes (no re-save). */
export function chatAttachmentFromStoredUpload(
  filename: string,
  mediaType: string,
  content: Buffer,
  source: { kind: string; id: string },
): ChatAttachment {
  return toChatAttachment(
    filename,
    mediaType,
    content,
    source,
    isUploadableTextFile(filename, mediaType),
  );
}

function normalizeIncomingMessageText(
  platform: string,
  message: Message,
): string {
  const text = message.text.trim();
  if (platform !== "slack" || !message.isMention) return text;

  // The Slack adapter leaves the bot's own unresolved @U… mention in plain
  // text so Chat SDK can detect app mentions. Do not pass that routing marker
  // through to the agent as user intent.
  return text.replace(/(^|\s)@[UW][A-Z0-9]+\b\s*/g, "$1").trim();
}

function toChatAttachment(
  filename: string,
  mediaType: string,
  content: Buffer,
  source: { kind: string; id: string },
  isText: boolean,
): ChatAttachment {
  if (isText) {
    return {
      kind: "text",
      filename,
      mediaType,
      content: content.toString("utf8").replace(/^\uFEFF/, ""),
      sizeBytes: content.byteLength,
      source,
    };
  }
  return {
    kind: "file",
    filename,
    mediaType,
    data: content,
    sizeBytes: content.byteLength,
    source,
  };
}
